import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Presentational rows for a Trailblazers treasure list.
 *
 * Deliberately pure: no context, no sockets, no game state — just
 * `TrlTreasureRow[]` in, markup out. That is what lets one component serve the
 * player's reveal overlay (a separate BrowserWindow with no app providers at
 * all), the player's in-app haul panel and the admin Results tab, so no two
 * surfaces can drift into describing the same reward differently.
 *
 * It lives here rather than in either app for that last reason: the admin tab
 * previously flattened an award list with `describeTrlTreasureItem` per item and
 * printed "Cartel pack · Cartel pack · …" once per copy, while the player saw a
 * single grouped row. Callers pass rows through `groupTrlTreasureItems` first;
 * this only draws them.
 *
 * Cartel icon urls arrive root-relative and are resolved against the caller's
 * API origin BEFORE they reach here — the two apps read that origin from
 * different config, and this component must stay free of both.
 *
 * Animation is NOT done here. The overlay stages its reveal purely in CSS off
 * the `--i` index each row carries, so the static haul panel gets the identical
 * markup with none of the motion.
 */
import { useState } from 'react';
import { getMapIconUrl, getTrlTreasureIconUrl } from '@swtorbit/shared-assets';
import { TRL_TREASURE_FALLBACK_GLYPH } from '@swtorbit/shared-socket-events';
/**
 * A reward's icon. Cartel rewards carry an uploaded image and credits/augments
 * their own bundled artwork — all drawn in the same frame at the same size;
 * custom ones reuse the bundled map-marker artwork (drawn as-authored, tinted
 * only in its halo, the same treatment `PlayerPlanetNode` gives a planet
 * marker). The bare glyph branch is now only a fallback.
 */
function TrlTreasureIcon({ icon }) {
    // An image can 404 — an uploaded cartel icon's catalogue entry may have been
    // replaced or deleted since the award was frozen — so a failed load falls back
    // to the row's glyph rather than leaving a broken image in the panel.
    const [imageFailed, setImageFailed] = useState(false);
    // Both image-backed kinds share one frame: an uploaded url, or a bundled asset
    // resolved from the id the row carries (the shared row type names ids rather
    // than urls, since the API server builds rows and cannot import images).
    const imageSrc = icon.kind === 'cartel'
        ? icon.url
        : icon.kind === 'asset'
            ? getTrlTreasureIconUrl(icon.asset)
            : undefined;
    if (imageSrc && !imageFailed) {
        return (_jsx("img", { className: "trl-treasure__icon-img", src: imageSrc, alt: "", onError: () => setImageFailed(true) }));
    }
    if (icon.kind === 'map-icon' && icon.id) {
        return (_jsx("span", { className: "trl-treasure__icon-art", style: {
                '--trl-icon-src': `url("${getMapIconUrl(icon.id)}")`,
                '--trl-treasure-icon-glow': icon.color ?? undefined,
            } }));
    }
    return (_jsx("span", { className: "trl-treasure__icon-glyph", children: icon.kind === 'glyph' || icon.kind === 'asset' ? icon.glyph : TRL_TREASURE_FALLBACK_GLYPH }));
}
/** The reward list itself — one row per grouped prize. */
export function TrlTreasureRows({ rows, staged = false }) {
    return (_jsx("div", { className: `trl-treasure__rows${staged ? ' trl-treasure__rows--staged' : ''}`, children: rows.map((row, index) => (_jsxs("div", { className: `trl-treasure__row trl-treasure__row--${row.tier}`, 
            // Read by the staggered reveal in _trl-treasure.scss.
            style: { '--i': index }, children: [_jsx("span", { className: "trl-treasure__icon", children: _jsx(TrlTreasureIcon, { icon: row.icon }) }), _jsxs("span", { className: "trl-treasure__detail", children: [_jsx("span", { className: `trl-treasure__name${row.quality ? ` text-quality-${row.quality}` : ''}`, children: row.name }), _jsx("span", { className: "trl-treasure__type", children: row.type })] }), _jsxs("span", { className: "trl-treasure__qty", children: ["\u00D7", row.quantity.toLocaleString('en-US')] })] }, row.key))) }));
}
